/**
 * "Record payment" — uitbetaling aan een chatter vastleggen.
 *
 * 16-09-2026 (Jordy). Het venster in het nieuwe dashboard had één bedragveld
 * en stuurde `{chatter_id, amount_paid, note}`. Dat kán niet werken: de server
 * (`MarkPaidRequest` in src/api/routes/admin_chatter_finance.py) eist ook
 * `period_start`, `period_end`, `gross_amount`, `commission_amount` en
 * `referral_amount` zonder standaardwaarde, dus elke poging kwam terug als
 * 422 en er is langs dit scherm nooit een betaling vastgelegd.
 *
 * Dit venster is nagebouwd naar het oude dashboard, dat per uitbetaling
 * vastlegt WAT er verdiend was en WAT er werkelijk betaald is:
 *
 *  - Periode (van/tot). De bedragen hangen er niet aan: die komen uit het
 *    openstaande saldo. De periode legt vast welk tijdvak je afrekent.
 *  - Sales (€) → `gross_amount`: de omzet waarover de commissie ging.
 *  - Total due (€) → `commission_amount`: eigen commissie + referral die nog
 *    openstaan. Alleen-lezen; het komt uit `sales-per-chatter-balances`.
 *  - Own commission due / Referral due: de uitsplitsing van dat bedrag.
 *    Referral gaat als `referral_amount` mee, want alleen dán verrekent
 *    `calculate_payout_balance` het referralsaldo (anders blijft het open).
 *  - Actually paid (€) → `amount_paid`: wat je echt overmaakt. Minder mag
 *    (deelbetaling), meer ook.
 *  - Is er meer betaald dan verschuldigd, dan bepaalt de keuze bonus/voorschot
 *    wat er met dat extra bedrag gebeurt: een bonus staat erbovenop, een
 *    voorschot (`extra_is_bonus: false`) wordt van toekomstige eigen commissie
 *    afgetrokken.
 *
 * Het portemonnee-blok erboven komt uit `compensation-roster` en is dezelfde
 * hulp als oud: het adres waar je naartoe betaalt, of de melding dat er nog
 * geen wallet staat.
 */
import { useEffect, useState } from 'react'
import { Modal, Field } from './Modal'
import { useActions, mut } from '../lib/actions'
import { api } from '../lib/api'

/** Eén regel uit `chatter-finance/sales-per-chatter-balances`. */
export interface PayoutBalance {
  chatter_id: number
  commission_pct: number | null
  open_commission: number
  open_own_commission: number
  open_referral_commission: number
  advance_remaining?: number | null
}
/** Eén eerder vastgelegde betaling uit `chatter-finance/payouts`. */
export interface EerdereBetaling {
  chatter_id: number
  period_end: string
}
/** De velden uit `compensation-roster` die dit venster gebruikt. */
export interface Wallet {
  chatter_id: number
  payout_wallet_address?: string | null
  payout_wallet_network?: string | null
}
/** Wat `chatter-finance/earnings/{id}` teruggeeft, voor zover hier nodig. */
interface Verdiensten {
  from?: string
  to?: string
  gross_total?: number
  commission_amount?: number
}

/** Vandaag als `YYYY-MM-DD`, in de tijdzone van de browser. */
export function vandaag(nu: Date = new Date()): string {
  const off = nu.getTimezoneOffset() * 60_000
  return new Date(nu.getTime() - off).toISOString().slice(0, 10)
}

/** `YYYY-MM-DD` plus een aantal dagen, zonder tijdzone-gedoe. */
export function plusDagen(dag: string, n: number): string {
  const d = new Date(dag + 'T12:00:00Z')
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** De overgangsdag (`commission_since`, naïef UTC zoals de server hem geeft)
 *  als kalenderdag in Nederland: 2026-09-15T22:00:00 → 2026-09-16. */
export function overgangsdag(commissionSince: string | null | undefined): string | null {
  if (!commissionSince) return null
  const s = String(commissionSince)
  const moment = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z')
  if (Number.isNaN(moment.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(moment)
}

/**
 * De dag waarop deze periode begint.
 *
 * 30-09-2026 (Jordy): de dag NÁ de laatste betaling — niet de dag zelf, dat
 * is al betaald — en nooit vóór de overgangsdag (commissie telt pas vanaf
 * 16-09). Zonder eerdere betaling de meegegeven terugval, ook niet vóór de
 * overgangsdag. `vorige` is de laatste betaalde dag (voor de uitleg).
 */
export function periodeStart(
  betalingen: EerdereBetaling[],
  chatterId: number,
  terugval: string,
  vanaf: string | null = null,
): { dag: string; vorige: string | null } {
  const eerder = betalingen
    .filter((b) => b.chatter_id === chatterId && b.period_end)
    .map((b) => String(b.period_end).slice(0, 10))
    .sort()
  const laatste = eerder.length ? eerder[eerder.length - 1] : null
  let dag = laatste ? plusDagen(laatste, 1) : terugval
  if (vanaf && (!dag || dag < vanaf)) dag = vanaf
  return { dag, vorige: laatste }
}

/** Wat er bóven de verschuldigde commissie wordt uitbetaald. */
export function extraBoven(betaald: unknown, verschuldigd: unknown): number {
  return Math.max(0, (Number(betaald) || 0) - (Number(verschuldigd) || 0))
}

/** Het formulier zoals het op het scherm staat. */
export interface PayVorm {
  chatter_id: number
  chatter_name: string
  period_start: string
  period_end: string
  gross_amount: string
  commission_amount: number
  open_own_commission: number
  open_referral_commission: number
  advance_remaining: number
  amount_paid: string
  extra_is_bonus: boolean
  note: string
}

/**
 * De body voor `POST chatter-finance/payouts/mark-paid`, exact zoals het oude
 * dashboard hem stuurt: hele dagen (00:00:00 tot 23:59:59) als ISO-tijd, en
 * referral uit het openstaande saldo, niet uit een los invoerveld.
 */
export function payload(v: PayVorm): Record<string, unknown> {
  return {
    chatter_id: v.chatter_id,
    period_start: new Date(v.period_start + 'T00:00:00').toISOString(),
    period_end: new Date(v.period_end + 'T23:59:59').toISOString(),
    gross_amount: Number(v.gross_amount) || 0,
    commission_amount: Number(v.commission_amount) || 0,
    amount_paid: Number(v.amount_paid) || 0,
    referral_amount: Number(v.open_referral_commission) || 0,
    extra_is_bonus: v.extra_is_bonus !== false,
    currency: 'EUR',
    note: v.note || null,
  }
}

/**
 * Het formulier vullen voor één chatter.
 *
 * Oud leidt Sales af uit de openstaande EIGEN commissie en het percentage
 * (`open_own / pct`), niet uit het gekozen tijdvenster van de pagina: het
 * bedrag hoort bij wat nog openstaat, niet bij wat je toevallig bekijkt.
 * Zonder saldo valt het terug op de verdiensten over het jaar.
 */
export function vulVorm(
  chatterId: number,
  naam: string,
  saldo: PayoutBalance | undefined,
  verdiensten: Verdiensten,
  betalingen: EerdereBetaling[],
  nu: Date = new Date(),
  vanaf: string | null = null,
): PayVorm {
  const dag = vandaag(nu)
  const pct = Number(saldo?.commission_pct) || 15
  const open = saldo?.open_commission
  const heeftSaldo = open != null && Number.isFinite(Number(open))
  const totaal = heeftSaldo ? Number(open) : Number(verdiensten.commission_amount ?? 0)
  const eigen = Number(saldo?.open_own_commission || 0)
  const omzet = heeftSaldo && pct > 0
    ? eigen / (pct / 100)
    : Number(verdiensten.gross_total ?? 0)
  const terugval = (verdiensten.from || '').slice(0, 10) || dag
  return {
    chatter_id: chatterId,
    chatter_name: naam,
    period_start: periodeStart(betalingen, chatterId, terugval, vanaf).dag,
    period_end: dag,
    gross_amount: (Math.round(omzet * 100) / 100).toFixed(2),
    commission_amount: Math.round(totaal * 100) / 100,
    open_own_commission: Math.round(eigen * 100) / 100,
    open_referral_commission: Math.round(Number(saldo?.open_referral_commission || 0) * 100) / 100,
    advance_remaining: Math.round(Number(saldo?.advance_remaining || 0) * 100) / 100,
    amount_paid: (Math.round(totaal * 100) / 100).toFixed(2),
    extra_is_bonus: true,
    note: '',
  }
}

const eur2 = (waarde: unknown) => '€' + (Number(waarde) || 0).toFixed(2)

export function PayoutDialog({
  chatterId,
  namen,
  balances,
  betalingen,
  wallets,
  commissionSince = null,
  onChatterChange,
  onClose,
  onDone,
}: {
  chatterId: number
  /** De overgangsdag zoals de saldo-route hem geeft; "Period from" komt
   *  nooit eerder uit. */
  commissionSince?: string | null
  /** chatter-id → naam, voor de keuzelijst en de titel. */
  namen: Map<number, string>
  balances: PayoutBalance[]
  betalingen: EerdereBetaling[]
  /** `null` zolang de roster niet geladen is (of niet mag: owner-only). */
  wallets: Wallet[] | null
  /** Een andere chatter kiezen; de ouder houdt bij wie open staat, zodat het
   *  formulier opnieuw wordt gevuld met diens saldo en periode. */
  onChatterChange: (id: number) => void
  onClose: () => void
  onDone: () => void
}) {
  const { run, busy, notify } = useActions()
  const [vorm, setVorm] = useState<PayVorm | null>(null)

  const saldo = balances.find((b) => b.chatter_id === chatterId)
  // Bij het openen, bij een andere chatter, en zodra de saldi binnen zijn:
  // saldo uit de meegegeven lijst, verdiensten uit de API (voor de terugval op
  // omzet en op de startdag). De afhankelijkheden zijn met opzet `saldo` en
  // het AANTAL betalingen: die blijven gelijk zolang de gegevens gelijk zijn,
  // terwijl de lijsten zelf per render een nieuw lege array kunnen zijn.
  useEffect(() => {
    let geldig = true
    const naam = namen.get(chatterId) ?? `#${chatterId}`
    const vanaf = overgangsdag(commissionSince)
    setVorm(vulVorm(chatterId, naam, saldo, {}, betalingen, new Date(), vanaf))
    api.get<Verdiensten>(`/api/admin/chatter-finance/earnings/${chatterId}?days=365`)
      .then((v) => {
        if (geldig) setVorm(vulVorm(chatterId, naam, saldo, v || {}, betalingen, new Date(), vanaf))
      })
      .catch(() => { /* zonder verdiensten blijft de terugval op vandaag staan */ })
    return () => { geldig = false }
  }, [chatterId, saldo, betalingen.length, commissionSince])

  if (vorm == null) return null
  const wijzig = (deel: Partial<PayVorm>) => setVorm((v) => (v ? { ...v, ...deel } : v))
  const wallet = (wallets ?? []).find((w) => w.chatter_id === chatterId)
  const adres = String(wallet?.payout_wallet_address || '').trim()
  const netwerk = String(wallet?.payout_wallet_network || '').trim()
  const extra = extraBoven(vorm.amount_paid, vorm.commission_amount)
  const tekort = extraBoven(vorm.commission_amount, vorm.amount_paid)
  const vorige = periodeStart(betalingen, chatterId, '').vorige
  /** Alles is al betaald tot en met vandaag: er valt geen periode af te rekenen. */
  const alBetaald = Boolean(vorige && vorige >= vandaag())
  const kanOpslaan = Boolean(vorm.period_start && vorm.period_end)
    && vorm.period_end >= vorm.period_start
    && !alBetaald
    && busy !== 'Record payment'

  return (
    <Modal
      title={`Record payment — ${vorm.chatter_name}`}
      desc="Sent as POST to chatter-finance/payouts/mark-paid, same as the old dashboard."
      width={620}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn primary sm"
            type="button"
            disabled={!kanOpslaan}
            onClick={() => run({
              label: 'Record payment',
              request: mut.post(
                '/api/admin/chatter-finance/payouts/mark-paid',
                payload(vorm),
              ),
              onDone: () => { onClose(); onDone() },
              successText: `Payment recorded for ${vorm.chatter_name}`,
            })}
          >
            Mark as paid
          </button>
        </>
      }
    >
      <Field label="Chatter">
        <select
          className="inp"
          value={String(chatterId)}
          onChange={(e) => onChatterChange(Number(e.target.value))}
        >
          {balances.map((b) => (
            <option key={b.chatter_id} value={b.chatter_id}>
              {namen.get(b.chatter_id) ?? b.chatter_id}
            </option>
          ))}
        </select>
      </Field>

      {adres ? (
        <div className="field">
          <label>Pay to{netwerk ? ` · ${netwerk}` : ''}</label>
          <div className="row" style={{ alignItems: 'center', gap: 8 }}>
            <span className="mono" style={{ flex: 1, wordBreak: 'break-all', fontSize: 12.5 }}>
              {adres}
            </span>
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(adres)
                notify('Wallet address copied')
              }}
            >
              Copy
            </button>
          </div>
        </div>
      ) : wallets != null ? (
        <div className="field">
          <span className="hint">
            No crypto wallet saved for this chatter — add one under Settings → Users.
          </span>
        </div>
      ) : null}

      {alBetaald ? (
        <div className="mut2" style={{ color: '#fcd34d', fontSize: 12.5, marginBottom: 8 }}>
          Everything is already paid up to {vorige}. There is no period left to record for
          this chatter today.
        </div>
      ) : null}
      <div className="row" style={{ gap: 12 }}>
        <Field label="Period from">
          <input className="inp" type="date" value={vorm.period_start}
                 onChange={(e) => wijzig({ period_start: e.target.value })} />
        </Field>
        <Field label="Period to">
          <input className="inp" type="date" value={vorm.period_end}
                 onChange={(e) => wijzig({ period_end: e.target.value })} />
        </Field>
      </div>
      <div className="field">
        <span className="hint">
          {vorige
            ? `Since last payment period ending ${vorige} → today.`
            : 'No earlier payment recorded for this chatter.'}
          {' '}Amounts still use open unpaid balance.
          {commissionSince ? ` Periods never start before ${overgangsdag(commissionSince)} (transition).` : ''}
        </span>
      </div>

      <div className="row" style={{ gap: 12 }}>
        <Field label="Sales (€)" hint="Revenue this commission was calculated over.">
          <input className="inp" type="number" step="0.01" value={vorm.gross_amount}
                 onChange={(e) => wijzig({ gross_amount: e.target.value })} />
        </Field>
        <Field label="Total due (€)" hint="Own commission + referral still unpaid">
          <input className="inp" type="number" readOnly value={vorm.commission_amount.toFixed(2)} />
        </Field>
      </div>

      <div className="row" style={{ gap: 12 }}>
        <Field label="Own commission due (€)" hint="Unpaid commission on this chatter's own sales">
          <input className="inp" type="number" readOnly
                 value={vorm.open_own_commission.toFixed(2)} />
        </Field>
        <Field label="Referral due (€)" hint="Included in Total due and cleared by this payment">
          <input className="inp" type="number" readOnly
                 value={vorm.open_referral_commission.toFixed(2)} />
        </Field>
      </div>

      <Field label="Actually paid (€)" hint="Amount you really pay out (e.g. incl. bonus/advance)">
        <input className="inp" type="number" step="0.01" value={vorm.amount_paid}
               onChange={(e) => wijzig({ amount_paid: e.target.value })} />
      </Field>

      <div className="field">
        <label>Extra above Total due: {eur2(extra)}</label>
        <select className="inp" value={vorm.extra_is_bonus === false ? 'advance' : 'bonus'}
                onChange={(e) => wijzig({ extra_is_bonus: e.target.value === 'bonus' })}>
          <option value="bonus">Bonus (support, not deducted)</option>
          <option value="advance">Advance (deducted later)</option>
        </select>
        {extra <= 0.005 ? (
          <span className="hint">
            {tekort > 0.005
              ? `Paying ${eur2(tekort)} less than due — the rest stays open.`
              : 'Only applies when you pay more than Total due.'}
          </span>
        ) : (
          <span className="hint">
            {vorm.extra_is_bonus
              ? 'Bonus: on top of the commission, never deducted later.'
              : 'Advance: deducted from this chatter’s future own commission.'}
          </span>
        )}
        {vorm.advance_remaining > 0.005 ? (
          <span className="hint" style={{ color: 'var(--acc2)' }}>
            Existing advance still covering future earnings: {eur2(vorm.advance_remaining)}
          </span>
        ) : null}
      </div>

      <Field label="Note (optional)">
        <input className="inp" value={vorm.note}
               onChange={(e) => wijzig({ note: e.target.value })} />
      </Field>
    </Modal>
  )
}
